import { useCallback, useEffect, useRef, useState } from 'react';
import {
  X,
  UserPlus,
  User,
  Mail,
  Phone,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import countriesData from '../../Data/CountryCodes.json';
/* In the real project this is YOUR shared dropdown component —
   same props contract, so the import path stays as-is. */
import CustomSelect from '../../Components/customs/dropdown';
import { useToast } from '../../Utils/toastContext';

/* ================================================================== */
/*  Invite member modal                                               */
/*                                                                    */
/*  POST /api/v1/agency/members/invite                                */
/*                                                                    */
/*  FIELD          REQUIRED   NOTES                                   */
/*  first_name     yes        1–100 characters                        */
/*  last_name      no                                                 */
/*  email_address  yes        Must be unused across the WHOLE         */
/*                            platform. Stored lower case.            */
/*  mobile_number  yes        Normalised (digits only in the payload) */
/*  country_code   no         Defaults to +61 — picked from the       */
/*                            shared CustomSelect fed by              */
/*                            Data/CountryCodes.json                  */
/*                                                                    */
/*  On success the modal closes immediately and toasts the API's      */
/*  message ("Admin invited successfully"). The one-time              */
/*  temp_password the API returns is deliberately NOT displayed or    */
/*  stored anywhere on the frontend.                                  */
/* ================================================================== */

const DEFAULT_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  cc: '+61',
  mobile: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputCls = (error, extra = '') =>
  `w-full h-11 rounded-xl border bg-white px-3.5 text-sm text-slate-800 placeholder:text-slate-400 shadow-sm transition-all duration-200 focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand/10 ${
    error
      ? 'border-red-300 bg-red-50/40 focus:ring-red-100'
      : 'border-slate-200 hover:border-slate-300 focus:bg-white'
  } ${extra}`;

function Field({ label, required, error, children }) {
  return (
    <label className="block">
      <span className="mb-1 flex items-center gap-1 text-[11px] font-semibold text-slate-500">
        {label}
        {required && <span className="text-red-500">*</span>}
        {error && (
          <span className="ml-1 font-medium text-red-500">{error}</span>
        )}
      </span>
      {children}
    </label>
  );
}

export default function InviteMemberModal({ open, onClose, onInvite }) {
  const { showToast } = useToast();

  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* Toast — always a 2-arg call with a lowercase, never-undefined type,
     wrapped so a toastProvider problem can't fail an invite. showToast
     is read from a ref so notify() keeps a stable identity. */
  const showToastRef = useRef(showToast);
  useEffect(() => {
    showToastRef.current = showToast;
  });

  const notify = useCallback((message, type = 'info') => {
    const text = String(message ?? '').trim();
    if (!text) return;
    const kind = String(type || 'info').toLowerCase();
    try {
      showToastRef.current?.(text, kind);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[InviteMemberModal] showToast threw:', err, {
        message: text,
        type: kind,
      });
    }
  }, []);

  /* Every open starts from a completely empty form */
  useEffect(() => {
    if (open) {
      setForm(DEFAULT_FORM);
      setErrors({});
      setApiError('');
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    const first = form.firstName.trim();
    if (!first) e.firstName = 'Required';
    else if (first.length > 100) e.firstName = 'Max 100 characters';
    if (!EMAIL_RE.test(form.email.trim())) e.email = 'Enter a valid email';
    if (!form.mobile.replace(/\D/g, '').trim()) e.mobile = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      first_name: form.firstName.trim(),
      email_address: form.email.trim().toLowerCase(), // API stores lower case
      mobile_number: form.mobile.replace(/\D/g, ''), // normalised like every invite
      country_code: form.cc,
    };
    if (form.lastName.trim()) payload.last_name = form.lastName.trim();

    setSubmitting(true);
    setApiError('');

    let toastMessage = null;
    try {
      const res = await onInvite(payload);
      toastMessage = {
        message: res?.message || 'Admin invited successfully.',
        type: 'success',
      };
      /* Close immediately — the temp_password in the response is ignored
         (never rendered, never stored). onClose(true) tells the parent
         to refresh the list. */
      onClose?.(true);
    } catch (err) {
      setApiError(err.message || 'Could not send the invite.');
      toastMessage = null; // the error stays visible inside the modal
    } finally {
      setSubmitting(false);
      if (toastMessage) {
        const { message, type } = toastMessage;
        setTimeout(() => notify(message, type), 300);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
        onClick={() => onClose?.(false)}
      />

      {/* Panel */}
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-lift ring-1 ring-slate-100 animate-modal-in max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="relative overflow-hidden rounded-t-2xl bg-gradient-to-r from-[#1B2668] via-[#375AE7] to-[#5B7CFA] p-5">
          <div className="absolute -top-12 right-4 w-36 h-36 rounded-full bg-white/10 blur-2xl pointer-events-none" />
          <div className="relative flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-white/15 ring-1 ring-white/25 flex items-center justify-center shrink-0">
                <UserPlus size={18} className="text-white" />
              </span>
              <div>
                <h2 className="text-base font-bold text-white">
                  Invite member
                </h2>
                <p className="text-[11px] text-white/70">
                  Adds an admin to your business and emails a temp password
                </p>
              </div>
            </div>
            <button
              onClick={() => onClose?.(false)}
              title="Close"
              className="w-8 h-8 rounded-lg text-white/70 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* ---------------- Form ---------------- */}
        <form onSubmit={submit} noValidate>
          <div className="space-y-3.5 p-5">
            {apiError && (
              <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5">
                <AlertTriangle
                  size={14}
                  className="mt-0.5 shrink-0 text-red-500"
                />
                <p className="text-[12px] font-semibold text-red-600">
                  {apiError}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="First name" required error={errors.firstName}>
                <div className="relative">
                  <User
                    size={14}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    value={form.firstName}
                    onChange={(e) => set('firstName', e.target.value)}
                    maxLength={100}
                    placeholder="Priya"
                    className={`${inputCls(!!errors.firstName)} pl-9`}
                  />
                </div>
              </Field>
              <Field label="Last name">
                <div className="relative">
                  <User
                    size={14}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    value={form.lastName}
                    onChange={(e) => set('lastName', e.target.value)}
                    maxLength={100}
                    placeholder="Menon"
                    className={`${inputCls(false)} pl-9`}
                  />
                </div>
              </Field>
            </div>

            <Field label="Email address" required error={errors.email}>
              <div className="relative">
                <Mail
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  placeholder="priya.menon@business.com"
                  className={`${inputCls(!!errors.email)} pl-9`}
                />
              </div>
              <span className="mt-1 block text-[10px] text-slate-400">
                Must be unused across the whole platform.
              </span>
            </Field>

            {/* Mobile — country code via the shared CustomSelect */}
            <Field label="Mobile" required error={errors.mobile}>
              <div className="flex gap-2">
                <CustomSelect
                  value={form.cc}
                  options={countriesData.map((c) => ({
                    value: c.dialCode,
                    label: `${c.code} ${c.dialCode}`,
                  }))}
                  onChange={(value) => set('cc', value)}
                  ariaLabel="Country code"
                  buttonClassName="h-11 w-28 rounded-xl px-2.5 text-sm font-semibold text-slate-700"
                />
                <input
                  value={form.mobile}
                  onChange={(e) =>
                    set('mobile', e.target.value.replace(/[^\d\s]/g, ''))
                  }
                  placeholder="412 345 678"
                  className={`${inputCls(!!errors.mobile, 'flex-1')}`}
                />
              </div>
            </Field>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2.5 border-t border-slate-100 p-4">
            <button
              type="button"
              onClick={() => onClose?.(false)}
              className="h-10 px-4 rounded-xl text-sm font-semibold text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex h-10 items-center gap-2 rounded-xl bg-gradient-to-r from-[#375AE7] to-[#5B7CFA] px-4 text-sm font-bold text-white shadow-md shadow-brand/25 transition-all hover:opacity-95 active:scale-[0.98] disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <UserPlus size={15} />
                  Send invite
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
